import { Alert, Button, Checkbox, Flex, Modal, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { confirmDestructive } from "@/common/confirm";
import type { AgentCredentialReveal } from "@/common/model";
import { COPYABLE } from "@/components/base";
import { AgentEnvBlock } from "./agent-env-block";

interface AgentCredentialModalProps {
	/** The one-time credential reveal from createAgent / rotateAgentCredential, or null when closed. */
	credentials: AgentCredentialReveal | null;
	onClose: () => void;
	/** Dialog title; defaults to the agent wording. Organization apps (ADR-061) pass their own. */
	title?: string;
	/** Show the CLI env-var block. On by default (agents); apps pass false — how they consume the credential is theirs. */
	showEnvBlock?: boolean;
}

/**
 * One-time reveal of a client credential: an External agent's (ADR-050) or an organization app's
 * (ADR-061). The platform stores only a hash, so this is the ONLY moment the secret is visible — the
 * modal requires an explicit "I saved it" acknowledgement before it can be dismissed, and never
 * reopens with the same secret.
 */
export function AgentCredentialModal({
	credentials,
	onClose,
	title = "Agent credential",
	showEnvBlock = true,
}: AgentCredentialModalProps) {
	const [acknowledged, setAcknowledged] = useState(false);

	// A fresh reveal always starts un-acknowledged, even if the modal is reused.
	useEffect(() => {
		if (credentials) setAcknowledged(false);
	}, [credentials]);

	const closeOrConfirm = () => {
		if (acknowledged) {
			onClose();
			return;
		}
		confirmDestructive({
			title: "Close without storing the client secret?",
			content:
				"It is not shown again. You would have to rotate it to get a new one.",
			okText: "Close anyway",
			onOk: onClose,
		});
	};

	return (
		<Modal
			title={title}
			open={!!credentials}
			// Closing without the acknowledgement asks first: the secret is never shown again.
			closable
			maskClosable={false}
			onCancel={closeOrConfirm}
			centered
			width={720}
			// Long content scrolls inside the body; the acknowledgement and Done stay in view.
			styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
			footer={
				<Flex align="center" justify="space-between" gap={16}>
					<Checkbox
						checked={acknowledged}
						onChange={(e) => setAcknowledged(e.target.checked)}
					>
						I stored the secret in a safe place.
					</Checkbox>
					<Button type="primary" disabled={!acknowledged} onClick={onClose}>
						Done
					</Button>
				</Flex>
			}
			destroyOnHidden
		>
			{credentials && (
				<Space orientation="vertical" size="middle" style={{ width: "100%" }}>
					<Alert
						type="warning"
						showIcon
						title="You won't be able to see this secret again."
						description="StratoNext stores only a hash. If the secret is lost, rotate the credential to get a new one."
					/>

					<div>
						<Typography.Text strong>Client ID</Typography.Text>
						<Typography.Paragraph
							code
							copyable={{ ...COPYABLE, text: credentials.clientId }}
							style={{ margin: 0 }}
						>
							{credentials.clientId}
						</Typography.Paragraph>
					</div>

					<div>
						<Typography.Text strong>Client secret</Typography.Text>
						<Typography.Paragraph
							code
							copyable={{ ...COPYABLE, text: credentials.clientSecret }}
							style={{ margin: 0, wordBreak: "break-all" }}
						>
							{credentials.clientSecret}
						</Typography.Paragraph>
					</div>

					{credentials.expiresAt ? (
						<Typography.Text>
							This secret expires on{" "}
							{new Date(credentials.expiresAt as string).toLocaleDateString()}.
						</Typography.Text>
					) : null}

					{showEnvBlock && (
						<Space vertical style={{ width: "100%" }}>
							<Typography.Text strong>Use it with the CLI</Typography.Text>
							<Typography.Text>
								Set these in the agent's own process environment (a service's
								environment file, CI secrets, or the MCP server's env block):
							</Typography.Text>
							{/* The copy carries the real secret; the display keeps it out of view. */}
							<AgentEnvBlock
								clientId={credentials.clientId}
								secret={credentials.clientSecret}
								displaySecret="<client secret>"
							/>
						</Space>
					)}
				</Space>
			)}
		</Modal>
	);
}
